import { A4_HEIGHT, A4_WIDTH } from '@/lib/constants/pdf.ts';
import type { FixedZoomAnchor, Point, ZoomAnchor } from '@/lib/types/zoom.ts';

export const getDistance = (a: Point, b: Point) => {
  return Math.hypot(a.x - b.x, a.y - b.y);
};

export const getCenterPoint = (a: Point, b: Point): Point => {
  return {
    x: (a.x + b.x) / 2,
    y: (a.y + b.y) / 2
  };
};

export const clamp = (value: number, min: number, max: number) => {
  return Math.min(Math.max(value, min), max);
};

export const roundZoomScale = (value: number) => {
  return Math.round(value * 100) / 100;
};

export const limitZoomScaleDelta = (currentZoomScale: number, targetZoomScale: number, maxDeltaPerFrame: number) => {
  const delta = targetZoomScale - currentZoomScale;

  if (Math.abs(delta) <= maxDeltaPerFrame) {
    return targetZoomScale;
  }

  return currentZoomScale + Math.sign(delta) * maxDeltaPerFrame;
};

export const getViewportPoint = (previewContainer: HTMLDivElement, point: Point): Point => {
  const rect = previewContainer.getBoundingClientRect();

  return {
    x: point.x - rect.left,
    y: point.y - rect.top
  };
};

export const createZoomAnchor = (previewContainer: HTMLDivElement, viewportPoint: Point, scale: number): ZoomAnchor => {
  return {
    contentX: (previewContainer.scrollLeft + viewportPoint.x) / scale,
    contentY: (previewContainer.scrollTop + viewportPoint.y) / scale,
    viewportX: viewportPoint.x,
    viewportY: viewportPoint.y
  };
};

export const getFixedZoomAnchor = (previewContainer: HTMLDivElement, point: Point, scale: number): FixedZoomAnchor => {
  const viewportPoint = getViewportPoint(previewContainer, point);
  const anchor = createZoomAnchor(previewContainer, viewportPoint, scale);

  return {
    contentX: anchor.contentX,
    contentY: anchor.contentY
  };
};

export const createTrackingZoomAnchor = (previewContainer: HTMLDivElement, fixedZoomAnchor: FixedZoomAnchor, point: Point): ZoomAnchor => {
  const viewportPoint = getViewportPoint(previewContainer, point);

  return {
    contentX: fixedZoomAnchor.contentX,
    contentY: fixedZoomAnchor.contentY,
    viewportX: viewportPoint.x,
    viewportY: viewportPoint.y
  };
};

export const applyZoomAnchor = (previewContainer: HTMLDivElement, anchor: ZoomAnchor, scale: number) => {
  const maxScrollLeft = Math.max(A4_WIDTH * scale - previewContainer.clientWidth, 0);
  const maxScrollTop = Math.max(A4_HEIGHT * scale - previewContainer.clientHeight, 0);
  const nextScrollLeft = clamp(anchor.contentX * scale - anchor.viewportX, 0, maxScrollLeft);
  const nextScrollTop = clamp(anchor.contentY * scale - anchor.viewportY, 0, maxScrollTop);

  previewContainer.scrollLeft = nextScrollLeft;
  previewContainer.scrollTop = nextScrollTop;
};

export const getNextFitScale = (width: number, height: number) => {
  const widthScale = width / A4_WIDTH;
  const heightScale = height / A4_HEIGHT;
  const nextScale = Math.min(widthScale, heightScale, 1);

  return nextScale || 1;
};
